import CloseSVG from '@public/icons/close-btn.svg';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { AnimatePresence, m as motion } from 'framer-motion';
import { FocusEventHandler, useEffect, useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { AnyObject, StringSchema, addMethod, number, object, string } from 'yup';
import { postStocks } from '@/shared/api/post-stocks';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Button from '@/shared/components/shared/Button';
import Checkbox from '@/shared/components/shared/Checkbox';
import Input from '@/shared/components/shared/Input';
import PersonalDataConsentLabel from '@/shared/components/shared/PersonalDataConsentLabel';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { DICT } from '@/shared/config/dict';
import { useGetCurrentUrl } from '@/shared/hooks/use-get-url';
import { useLocale } from '@/shared/hooks/use-locale';
import { useSessionStorage } from '@/shared/hooks/use-session-storage';
import { FeedbackResponse } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { formatPhoneHref } from '@/shared/lib/utils/strings';
import { isPhone } from '@/shared/lib/utils/validation/phone';
import Image from '../../shared/Image';

export const STOCKS_FORM_POPUP = 'stocks-form-popup';

export type StocksDataProps = {
    title: string;
    description?: string;
    formDescription?: string;
    timeDuration?: number;
};

interface Props extends React.HTMLAttributes<HTMLElement> {
    StocksProps: StocksDataProps;
}
addMethod(string, 'customPhone', function (errorMessage) {
    return this.test(`test-custom-phone`, errorMessage, function (value) {
        return typeof value === 'string' ? isPhone(value) : false;
    });
});

function StocksFormPopup({ StocksProps }: Props) {
    const locale = useLocale();
    const TIME_DURATION = StocksProps?.timeDuration ? StocksProps.timeDuration * 1000 : 14000;

    const currentUrl = useGetCurrentUrl();
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [response, setResponse] = useState<FeedbackResponse | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { closePopup } = useSetOpenedPopupsState();
    const { openPopup } = useSetOpenedPopupsState();

    const [stocksData, setStocksData] = useSessionStorage<boolean>('stocks-data');

    const [utmParams, setUtmParams] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');

    useEffect(() => {
        const fetchUtmParams = () => {
            const stored = localStorage.getItem('utmParams');
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    if (typeof parsed === 'object' && parsed !== null) {
                        setUtmParams(parsed);

                        setIsLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error('Ошибка парсинга utmParams', e);
                }
            }
            const timer = setTimeout(() => {
                fetchUtmParams();
            }, TIME_DURATION / 2);
            return () => clearTimeout(timer);
        };

        fetchUtmParams();
    }, [StocksProps]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (StocksProps.title && !stocksData) {
            timeoutId = setTimeout(() => {
                openPopup(STOCKS_FORM_POPUP);
            }, TIME_DURATION);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [StocksProps.title, stocksData, openPopup]);

    useEffect(() => {
        if (response) {
            setTimeout(() => closePopup(STOCKS_FORM_POPUP), TIME_DURATION);
        }
    }, [response, closePopup]);

    const schema = object({
        name: string().required('Поле обязательно к заполнению'),
        company: string().required('Поле обязательно к заполнению'),
        email: string()
            .required('Поле обязательно к заполнению')
            .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, 'Введите корректный адрес эл. почты'),
        phone: (
            string().required('Поле обязательно к заполнению') as StringSchema<string, AnyObject, undefined, ''> & {
                customPhone: any;
            }
        ).customPhone('Номер телефона введен некорректно'),
        agreementChecked: number().oneOf([1], DICT.FILL_CHECKED[locale]),
        agreementCheckedProcessingPersonalData: number().oneOf([1], DICT.FILL_CHECKED[locale]),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            company: '',
            phone: '',
            agreementChecked: 0,
            agreementCheckedProcessingPersonalData: 0,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            clearTimeout(timeoutRef.current);
            setResponse(null);
            const transformedData = {
                ...values,
                name: values.name,
                email: values.email,
                company: values.company,
                agreementChecked: values.agreementChecked,
                agreementCheckedProcessingPersonalData: values.agreementCheckedProcessingPersonalData,
                phone: Number(formatPhoneHref(values.phone).replace('+', '')),

                url: currentUrl.length > 0 ? currentUrl : '',
                ...Object.fromEntries(Object.entries(utmParams).filter(([_, value]) => value)),
            };

            window._txq?.push(['track', 'otpravit_vigoda50']);

            try {
                const data = await postStocks(transformedData);

                setResponse(data);

                if (data.status === 'success') {
                    setValues(initialValues);
                    setTouched(initialTouched);
                    setStocksData(true);
                } else {
                    setResponse({
                        status: 'error',
                        message: data.errors?.flat().join('<br>') ?? 'Что-то пошло не так',
                    });
                }
            } catch (err) {
                if (err instanceof AxiosError) {
                    setResponse({
                        status: 'error',
                        message: err.response?.data.errors?.flat().join('<br>') ?? 'Что-то пошло не так',
                    });
                } else {
                    throw err;
                }
            } finally {
                timeoutRef.current = setTimeout(() => {
                    setResponse(null);
                }, 5000);
            }
        },
    });

    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldTouched,
        setValues,
        setTouched,
        initialTouched,
        initialValues,
        setFieldValue,
    } = formik;

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
        setFieldTouched(event.target.name, false);
    };

    return (
        StocksProps.title && (
            <Popup name={STOCKS_FORM_POPUP} data-popup-preset="default" className="stocks-form-popup" overlay={true}>
                <button
                    aria-label="Закрыть"
                    className="menu-popup-close-btn"
                    onClick={() => {
                        closePopup(STOCKS_FORM_POPUP);
                    }}
                >
                    <CloseSVG />
                </button>
                <div className="stocks-form-popup__container">
                    <div className="stocks-form-popup__left">
                        <Image
                            src={
                                isMobile
                                    ? '/img/stocks-popup/gradient-horisontal.png'
                                    : '/img/stocks-popup/gradient.png'
                            }
                            className="stocks-form-popup__left-background"
                            alt=""
                            width={1}
                            height={1}
                            sizes="(max-width: 767px) 90vw, (max-width: 1360px) 30vw, 400px"
                        />
                        <h2
                            className="stocks-form-popup-title text-xl"
                            dangerouslySetInnerHTML={{
                                __html: tp(StocksProps.title),
                            }}
                        ></h2>
                        <div
                            className="stocks-form-popup__description text-xs"
                            dangerouslySetInnerHTML={{
                                __html: tp(StocksProps.description || ''),
                            }}
                        ></div>
                    </div>
                    <div className="stocks-form-popup__right">
                        <form ref={formRef} className="stocks-form-popup-form" noValidate onSubmit={handleSubmit}>
                            <div className="stocks-inputs-block">
                                <div
                                    className="stocks-inputs-block__title text-m"
                                    dangerouslySetInnerHTML={{
                                        __html: tp(
                                            StocksProps.formDescription ||
                                                `
                                        Заполните форму, и&nbsp;мы&nbsp;сообщим вам<br/> детальные условия участия.
                                        `,
                                        ),
                                    }}
                                ></div>
                                <div className="organize-input-block stocks-input-block text-s">
                                    <div className="stocks-input-name text-s">Имя *</div>
                                    <Input
                                        label=""
                                        type="text"
                                        name="name"
                                        className="organize-form-popup-input"
                                        id="stocks-form-name"
                                        placeholder="Иван"
                                        value={values.name}
                                        onInput={handleChange}
                                        onChange={handleChange}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.name ? errors.name : ''}
                                        valid={!(touched.name && errors.name)}
                                    />
                                </div>

                                <div className="organize-input-block stocks-input-block text-s">
                                    <div className="stocks-input-name text-s">Email *</div>
                                    <Input
                                        type="email"
                                        name="email"
                                        className="organize-form-popup-input"
                                        placeholder={'tnf2024@gmail.com'}
                                        required
                                        label=""
                                        disabled={isSubmitting}
                                        id="stocks-form-email"
                                        value={values.email}
                                        onInput={handleChange}
                                        onChange={handleChange}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.email ? errors.email : ''}
                                        valid={!(!!touched.email && !!errors.email)}
                                    />
                                </div>
                                <div className="organize-input-block stocks-input-block text-s">
                                    <div className="stocks-input-name text-s">Телефон *</div>
                                    <IMaskInput
                                        id="stocks-form-phone"
                                        mask="+7 (000) 000-00-00"
                                        lazy={false}
                                        placeholder="+7 (999) 999-99-99"
                                        value={values.phone}
                                        onAccept={(value: string) => setFieldValue('phone', value)}
                                        onBlur={handleBlur}
                                        onFocus={onFocus}
                                        name="phone"
                                        className={classNames('organize-form-popup-input', {
                                            'is-error': touched.phone && errors.phone,
                                        })}
                                    />
                                    <div className="app-message text-xs" aria-live="polite">
                                        {touched.phone ? errors.phone : ''}
                                    </div>
                                </div>

                                <div className="organize-input-block stocks-input-block text-s">
                                    <div className="stocks-input-name text-s">Название компании *</div>
                                    <Input
                                        label=""
                                        type="text"
                                        name="company"
                                        id="stocks-form-company"
                                        className="organize-form-popup-input"
                                        placeholder="TNF / 7743013902"
                                        value={values.company}
                                        onInput={handleChange}
                                        onChange={handleChange}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.company ? errors.company : ''}
                                        valid={!(touched.company && errors.company)}
                                    />
                                </div>
                            </div>
                            <div className="stocks-form-popup-privacy">
                                <div className="form-checkbox-wrapper">
                                    <Checkbox
                                        className="agreement-checkbox"
                                        name="agreementChecked"
                                        checked={values.agreementChecked === 1}
                                        onChange={(e) => {
                                            setFieldTouched('agreementChecked', true);
                                            handleChange({
                                                target: {
                                                    name: e.target.name,
                                                    value: e.target.checked ? 1 : 0,
                                                },
                                            });
                                        }}
                                        onBlur={handleBlur}
                                        valid={!errors.agreementChecked}
                                    >
                                        <span className="contact-us__checkbox-block__text text-xs">
                                            <PersonalDataConsentLabel />
                                        </span>
                                    </Checkbox>
                                    <p className="privacy-checkbox"></p>
                                </div>
                                <div className="form-checkbox-wrapper">
                                    <Checkbox
                                        className="agreement-checkbox"
                                        name="agreementCheckedProcessingPersonalData"
                                        checked={values.agreementCheckedProcessingPersonalData === 1}
                                        onChange={(e) => {
                                            setFieldTouched('agreementCheckedProcessingPersonalData', true);
                                            handleChange({
                                                target: {
                                                    name: e.target.name,
                                                    value: e.target.checked ? 1 : 0,
                                                },
                                            });
                                        }}
                                        onBlur={handleBlur}
                                        valid={!errors.agreementCheckedProcessingPersonalData}
                                    >
                                        <span className="contact-us__checkbox-block__text text-xs">
                                            Принимаю условия{' '}
                                            <a
                                                href="/privacy/"
                                                className="link link--underlined"
                                                target="_blank"
                                                rel="noreferrer nofollow"
                                            >
                                                пользовательского соглашения
                                            </a>
                                        </span>
                                    </Checkbox>
                                    <p className="privacy-checkbox"></p>
                                </div>
                                <Button
                                    type="submit"
                                    variant="outline-dark"
                                    disabled={isSubmitting}
                                    className="stocks-form-popup-submit"
                                >
                                    Отправить
                                </Button>
                            </div>
                            <AnimatePresence>
                                {response && (
                                    <motion.div
                                        className="organize-detail-popup-response"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        {response.status === 'success' ? (
                                            <>
                                                <p className="text-l">
                                                    <Typografed>Форма успешно отправлена</Typografed>
                                                </p>
                                                {response.message && (
                                                    <p
                                                        className="text-s"
                                                        dangerouslySetInnerHTML={{ __html: tp(response.message) }}
                                                    ></p>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-l">Произошла ошибка</p>
                                                {response.message && (
                                                    <div
                                                        className="text-s stocks-form-error-message"
                                                        dangerouslySetInnerHTML={{ __html: tp(response.message) }}
                                                    ></div>
                                                )}
                                                <>
                                                    <Button
                                                        type="button"
                                                        variant="primary-filled"
                                                        onClick={() => {
                                                            setResponse(null);
                                                        }}
                                                    >
                                                        Отправить снова
                                                    </Button>
                                                    <a
                                                        href={'https://t.me/tnf_oilgasforum_bot'}
                                                        className="text-xs"
                                                        target="_blank"
                                                        rel="noreferrer nofollow"
                                                    >
                                                        Служба поддержки TNF
                                                    </a>
                                                </>
                                            </>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </div>
                </div>
            </Popup>
        )
    );
}

export default StocksFormPopup;
